import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined, HistoryOutlined } from "@ant-design/icons"
import type React from "react"
import PointTransactionSearchForm from "./components/PointTransactionSearchForm"
import PointTransactionTable from "./components/PointTransactionTable"
import {
  pointTransactionService,
  type PointTransactionQueryParams,
} from "@/services/gamification/pointTransactionApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const PointTransactionPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<PointTransactionQueryParams>({
    ...PAGE_CONFIG,
    search: "",
    event_type: undefined,
  })

  const [total, setTotal] = useState<number>(0)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["point_transactions_list", searchParams],
    queryFn: () => {
      return pointTransactionService.list({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
        event_type: searchParams.event_type || undefined,
      })
    },
  })

  const handleSearch = (values: Omit<PointTransactionQueryParams, "page" | "limit">) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
    })
  }

  const items = result?.data || []
  const totalCount = result?.total ?? total

  return (
    <div className="flex flex-col gap-2">
      <PointTransactionSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          event_type: searchParams.event_type,
        }}
      />

      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <HistoryOutlined className="text-amber-500 text-lg" />
              <span className="font-semibold text-base text-gray-800">Lịch sử Biến động Điểm (Point Transactions)</span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số giao dịch: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button
              icon={<ReloadOutlined spin={isFetching} />}
              onClick={() => refetch()}
              loading={isFetching}
            >
              Làm mới
            </Button>
          </div>
        }
      >
        <PointTransactionTable
          data={items}
          loading={isFetching}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>
    </div>
  )
}

export default PointTransactionPage
